/**
 * Error Handler Utility
 * Parse detailed error messages and return user-friendly common errors
 */

/**
 * Get common error message from error object
 * @param {Error} error - Error object from API call (axios or fetch)
 * @param {Function} t - i18n translation function
 * @returns {string} - User-friendly error message
 */
export const getCommonErrorMessage = (error, t) => {
  // Default error message
  let errorKey = 'errors.common.unknown';

  // Check if error response exists (axios)
  if (error.response) {
    const status = error.response.status;
    
    // Handle HTTP status codes
    if (status === 400) {
      errorKey = 'errors.common.badRequest';
    } else if (status === 401) {
      errorKey = 'errors.common.unauthorized';
    } else if (status === 403) {
      errorKey = 'errors.common.forbidden';
    } else if (status === 404) {
      errorKey = 'errors.common.notFound';
    } else if (status === 500) {
      errorKey = 'errors.common.serverError';
    } else if (status === 503) {
      errorKey = 'errors.common.serviceUnavailable';
    } else if (status >= 400 && status < 500) {
      errorKey = 'errors.common.clientError';
    } else if (status >= 500) {
      errorKey = 'errors.common.serverError';
    }
  } else if (error.request) {
    // Request was made but no response received (axios)
    errorKey = 'errors.common.networkError';
  } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
    // Fetch API network error
    errorKey = 'errors.common.networkError';
  }

  // Log the detailed error for debugging (only in console)
  console.error('Detailed error:', error.response?.data || error.message);

  return t(errorKey);
};

/**
 * Get specific error message for chat operations
 * @param {Error} error - Error object
 * @param {Function} t - i18n translation function
 * @returns {string} - User-friendly error message
 */
export const getChatErrorMessage = (error, t) => {
  if (error.message && error.message.includes('stream')) {
    return t('errors.chat.streamError');
  }
  return getCommonErrorMessage(error, t);
};

/**
 * Get specific error message for API key settings
 * @param {Error} error - Error object
 * @param {Function} t - i18n translation function
 * @returns {string} - User-friendly error message
 */
export const getApiKeyErrorMessage = (error, t) => {
  if (error.response?.status === 400) {
    return t('errors.apiKey.invalid');
  }
  return getCommonErrorMessage(error, t);
};

/**
 * Get specific error message for RAG operations
 * @param {Error} error - Error object
 * @param {Function} t - i18n translation function
 * @returns {string} - User-friendly error message
 */
export const getRagErrorMessage = (error, t) => {
  if (error.response?.status === 413) {
    return t('errors.rag.fileTooLarge');
  }
  return getCommonErrorMessage(error, t);
};
